import SearchForm from "components/SearchFilters/SearchForm";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TabFilters from "./TabFilters";

interface FiltersPageProps {
  onApplyFilters: (filters: any) => void;
  onApplyFiltersRegionEvent: (filters: any) => void;
}

const FiltersPage: React.FC<FiltersPageProps> = ({ onApplyFilters, onApplyFiltersRegionEvent }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [shouldClear, setShouldClear] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [clearClicked, setClearClicked] = useState(false);

  // Initialize state from URL parameters
  const [title, setTitle] = useState(searchParams.get("title") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [visitor, setVisitor] = useState(searchParams.get("visitor") || "");
  const [minPrice, setMinPrice] = useState(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") || "");
  const [event, setEvent] = useState(searchParams.get("event") || "");
  const [region, setRegion] = useState(searchParams.get("region") || "");

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
  };

  const mobileContainerStyle = {
    flexDirection: "column",
  } as React.CSSProperties;

  const responsiveContainerStyle = {
    ...containerStyle,
    ...((window.innerWidth || document.documentElement.clientWidth) < 600 && mobileContainerStyle),
  };

  // Function to update URL with current filter values
  const updateURL = (filters: any) => {
    const params = new URLSearchParams(location.search);

    // Update or remove parameters based on filter values
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key] !== "") {
        params.set(key, filters[key].toString());
      } else {
        params.delete(key);
      }
    });

    // Keep page parameter if it exists, otherwise reset to 1
    if (!params.get("page")) {
      params.set("page", "1");
    }

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleApplyCategories = (selectedCategories: any) => {
    setCategory(selectedCategories);
    updateURL({
      category: selectedCategories,
      visitor,
      minPrice,
      maxPrice,
      title,
      event,
      region,
      page: 1, // Reset to first page when filters change
    });
  };

  const handleApplyVisitors = (selectedVisitors: any) => {
    setVisitor(selectedVisitors);
    updateURL({
      category,
      visitor: selectedVisitors,
      minPrice,
      maxPrice,
      title,
      event,
      region,
      page: 1, // Reset to first page when filters change
    });
  };

  const handleApplyPrice = (selectedPrice: any) => {
    if (selectedPrice === "") {
      setMinPrice("");
      setMaxPrice("");
      updateURL({
        category,
        visitor,
        minPrice: "",
        maxPrice: "",
        title,
        event,
        region,
        page: 1,
      });
    } else {
      const [min, max] = selectedPrice.split(" - ").map(Number);
      setMinPrice(min.toString());
      setMaxPrice(max.toString());
      updateURL({
        category,
        visitor,
        minPrice: min.toString(),
        maxPrice: max.toString(),
        title,
        event,
        region,
        page: 1, // Reset to first page when filters change
      });
    }
  };

  const handleApplyRegion = (field: string, selectedValue: string) => {
    if (field === "region") {
      setRegion(selectedValue);
    } else if (field === "event") {
      setEvent(selectedValue);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setTitle("");
    setCategory("");
    setVisitor("");
    setMinPrice("");
    setMaxPrice("");
    setEvent("");
    setRegion("");

    // Clear ALL URL parameters and only keep page=1
    const params = new URLSearchParams(location.search);

    // Clear all filter parameters
    params.delete("title");
    params.delete("category");
    params.delete("visitor");
    params.delete("minPrice");
    params.delete("maxPrice");
    params.delete("event");
    params.delete("region");

    // Set page to 1
    params.set("page", "1");

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    onApplyFilters({});
    onApplyFiltersRegionEvent({});
  };

  // Apply filters when component mounts or URL changes
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const urlCategory = currentParams.get("category") || "";
    const urlVisitor = currentParams.get("visitor") || "";
    const urlMinPrice = currentParams.get("minPrice") || "";
    const urlMaxPrice = currentParams.get("maxPrice") || "";
    const urlTitle = currentParams.get("title") || "";
    const urlEvent = currentParams.get("event") || "";
    const urlRegion = currentParams.get("region") || "";

    // Update state from URL
    setCategory(urlCategory);
    setVisitor(urlVisitor);
    setMinPrice(urlMinPrice);
    setMaxPrice(urlMaxPrice);
    setTitle(urlTitle);
    setEvent(urlEvent);
    setRegion(urlRegion);

    // Apply filters
    onApplyFilters({
      minPrice: urlMinPrice,
      maxPrice: urlMaxPrice,
      visitor: urlVisitor,
      category: urlCategory,
      title: urlTitle,
    });

    if (urlEvent || urlRegion) {
      onApplyFiltersRegionEvent({ event: urlEvent, region: urlRegion });
    }
  }, [location.search]);

  useEffect(() => {
    if (searchClicked) {
      updateURL({
        category,
        visitor,
        minPrice,
        maxPrice,
        title,
        event: event,
        region: region,
        page: 1,
      });
      onApplyFiltersRegionEvent({ event: event, region: region });
      setSearchClicked(false);
    }
    if (clearClicked) {
      handleClearAllFilters();
      setClearClicked(false);
    }
  }, [searchClicked, clearClicked]);

  return (
    <div className="mt-2">
      <SearchForm
        fields={{ region: true, event: true }}
        onSelect={handleApplyRegion}
        searchClicked={searchClicked}
        handleSearchClick={() => setSearchClicked(true)}
        handleDelete={() => setClearClicked(true)}
      />
      <div style={responsiveContainerStyle} className="mt-2">
        <TabFilters
          onApply={handleApplyCategories}
          onApplyVisitor={handleApplyVisitors}
          onApplyPrice={handleApplyPrice}
          handleDelete={clearClicked}
        />
      </div>
    </div>
  );
};

export default FiltersPage;
